import React, { useState, useEffect } from 'react';
import { fetchSignalHistory } from '../../services/api';

const SignalHistory = ({ websocket, selectedAsset, selectedAccount, timeframe, userProfile }) => {
  const [signals, setSignals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const loadSignals = async () => {
    if (!selectedAsset || !selectedAccount) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const accountName = selectedAsset.broker === 'okx' ? 
        userProfile.name :
        selectedAccount.account;
      
      
      const result = await fetchSignalHistory(
        accountName,
        selectedAsset.broker,
        selectedAsset.symbol
      );
      
      if (result && result.signals && Array.isArray(result.signals)) {
        const filtered = timeframe 
          ? result.signals.filter(s => s.timeframe === timeframe) 
          : result.signals;
        
        setSignals(filtered);
      } else {
        setSignals([]);
      }
    } catch (error) {
      console.error('Error loading signals:', error);
      setError(error.message);
      setSignals([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadSignals();
  }, [selectedAsset, selectedAccount, timeframe]);
  
  useEffect(() => {
    if (!websocket || !selectedAsset || !selectedAccount) return;
    
    const handleWebSocketMessage = (event) => {
      if (event.data === 'ping') return;
      
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        if (data.type === 'initial_signals' && Array.isArray(data.signals)) {
          const filteredSignals = timeframe 
            ? data.signals.filter(s => s.timeframe === timeframe)
            : data.signals;
          
          setSignals(filteredSignals);
        } else if (data.type === 'signal_update' && data.signal) {
          if (data.signal.broker === selectedAsset.broker && 
              data.signal.symbol === selectedAsset.symbol &&
              (!timeframe || data.signal.timeframe === timeframe)) {
            
            setSignals(prev => {
              const newSignals = [data.signal, ...prev];
              return newSignals.slice(0, 21);
            });
          }
        }
      } catch (error) {
        console.error('WebSocket message processing error:', error);
      }
    };
    
    websocket.addEventListener('message', handleWebSocketMessage);
    return () => websocket.removeEventListener('message', handleWebSocketMessage);
  }, [websocket, selectedAsset, selectedAccount, timeframe]);
  
  if (!selectedAsset || !selectedAccount) return null;
  
  const enrichedSignals = signals.map((signal, index) => {
    if (signal.source_name && signal.source_name.toLowerCase() === 'x3') {
      return { ...signal, displaySide: signal.side };
    }
    const prevX3 = signals.slice(index).find(s => 
      s.source_name && s.source_name.toLowerCase() === 'x3'
    );
    return { 
      ...signal, 
      displaySide: prevX3 ? prevX3.side : signal.side 
    };
  });
  
  return (
    <div className="bg-gray-50 rounded-b-lg shadow-md px-1 py-1 mb-4" style={{ width: 'calc(100% - 1rem)' }}>
      <div className="text-xs text-gray-500 mb-1">
        Signals {timeframe || ''}
      </div>
      
      <div className="flex overflow-x-auto whitespace-nowrap pb-1 w-full">
        {isLoading ? (
          <div className="text-xs text-gray-500 italic py-1">Loading...</div>
        ) : error ? (
          <div className="text-xs text-red-500 italic py-1">Error: {error}</div>
        ) : signals.length === 0 ? (
          <div className="text-xs text-gray-500 italic py-1">No signals</div>
        ) : (
          enrichedSignals.map((signal, index) => (
            <div 
              key={index} 
              className={`flex-shrink-0 inline-flex items-center mr-2 px-2 py-1 rounded ${
                signal.displaySide.toLowerCase() === 'buy'
                  ? 'bg-green-50 border-l-2 border-green-500' 
                  : 'bg-red-50 border-l-2 border-red-500'
              }`}
            >
              <span className="font-medium text-xs">{signal.source_name}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SignalHistory;